import { useCallback } from 'react'
import { useMutation, useReactiveVar } from '@apollo/client'
import { useParams } from 'react-router-dom'
import set from 'lodash/fp/set'

import { TRouterParams } from 'shared/types'
import { countryCodeVar } from 'modulesCommon/store'
import { UPDATE_PRODUCT, GET_PRODUCTS_BY_CAMPAIGN } from '../constants'
import { IProductsByCampaign, IUpdateProductArgument } from '../types'

function updateList<T extends IProductsByCampaign>(record: T, list?: ReadonlyArray<T>): ReadonlyArray<T> {
  if (typeof list === 'undefined') return []
  const idx = list.findIndex((f) => f.parent_id === record.id)
  if (idx < 0) return list
  const { id, parent_id } = list[idx]
  return set(idx)({ ...record, id, parent_id })(list)
}

export const useProductUpdate = (updateCache: boolean = true) => {
  const { id: project_id } = useParams<TRouterParams>()
  const countryCode = useReactiveVar(countryCodeVar)

  const [updateProductItem, { loading, error }] = useMutation<
    { updateProduct: IProductsByCampaign },
    { input: IUpdateProductArgument }
  >(UPDATE_PRODUCT, {
    update(cache, { data }) {
      if (updateCache && typeof data?.updateProduct !== 'undefined') {
        const res = cache.readQuery<{ getProductsByCampaignId: IProductsByCampaign[] }>({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode }
        })
        cache.writeQuery({
          query: GET_PRODUCTS_BY_CAMPAIGN,
          variables: { campaignId: project_id, countryCode },
          data: {
            getProductsByCampaignId: updateList(data.updateProduct, res?.getProductsByCampaignId)
          }
        })
      }
    }
  })

  const updateProduct = useCallback<(data: IProductsByCampaign) => Promise<IProductsByCampaign | undefined>>(
    async ({ configuration, gpid_list, gps_status, id, name, parent_id, ...rest }) => {
      const { data } = await updateProductItem({
        variables: {
          input: {
            id: parent_id,
            name,
            gps_status,
            gpid_list,
            configuration,
            local: {
              id,
              active: rest.active,
              main: false,
              publish_date: rest.publish_date,
              radio_updates: rest.radio_updates,
              dsp_updates: rest.dsp_updates
            }
          }
        }
      })
      return data?.updateProduct
    },
  [updateProductItem]
  )

  return {
    updateProduct,
    loading,
    error
  }
}
